import { useCallback, useEffect, useRef, useState } from "react"

import { useScopedStorageKey } from "@/workspaces/scope"

import type { ConversationsTableResult } from "@/types/tables/ConversationsTable"

import type { ColumnCallbacks } from "../types/filterState"
import { CLUSTER_DESCRIPTION_COLUMN_KEY } from "../utils/conversationClusterDescriptions"
import { savedViewsStorage } from "../utils/savedViewsStorage"

const REVIEWER_COLUMN_KEY = "reviewer_assignees"

const BASE_COLUMNS = new Set([
  "simulation_output_id",
  "created_at",
  "occurred_at",
  "audio",
  "tags",
  "customer_metadata",
])

interface UseConversationsColumnsProps {
  allConversationsData: ConversationsTableResult[]
  defaultConversationMetrics: string[]
  /** Latest clustering run has a description for at least one conversation. */
  hasClusterDescriptions: boolean
  registerColumnCallbacks: (callbacks: ColumnCallbacks) => () => void
}

interface UseConversationsColumnsResult {
  visibleColumns: Set<string>
  hasReviewerAssigneesInLoadedRows: boolean
  handleVisibleColumnsReset: (columns: Set<string>) => void
}

function hasReviewerAssignees(
  allConversationsData: ConversationsTableResult[],
): boolean {
  return allConversationsData.some(
    (item) => (item.reviewer_assignees?.length ?? 0) > 0,
  )
}

function getDefaultBaseColumns(
  allConversationsData: ConversationsTableResult[],
): Set<string> {
  const columns = new Set(BASE_COLUMNS)
  if (hasReviewerAssignees(allConversationsData)) {
    columns.add(REVIEWER_COLUMN_KEY)
  }
  return columns
}

function deriveVisibleColumnsFromSavedState(
  currentStateStorageKey: string | undefined,
  allConversationsData: ConversationsTableResult[],
): Set<string> {
  const savedState = savedViewsStorage.getCurrentState(currentStateStorageKey)
  const savedVisibleColumns = savedState?.display?.visibleColumns
  if (
    savedVisibleColumns &&
    Array.isArray(savedVisibleColumns) &&
    savedVisibleColumns.length > 0
  ) {
    const savedSet = new Set(
      savedVisibleColumns.filter((column) => !column.startsWith("metadata_")),
    )
    // Before Uploaded and Created became separate columns, `created_at`
    // represented whichever timestamp mode was selected. Existing local state
    // should start with both real columns visible after the migration.
    if (savedSet.has("created_at")) {
      savedSet.add("occurred_at")
    }
    savedSet.delete("status")
    return savedSet.size > 0
      ? savedSet
      : getDefaultBaseColumns(allConversationsData)
  }
  return getDefaultBaseColumns(allConversationsData)
}

export function useConversationsColumns({
  allConversationsData,
  defaultConversationMetrics,
  hasClusterDescriptions,
  registerColumnCallbacks,
}: UseConversationsColumnsProps): UseConversationsColumnsResult {
  const hasReviewerAssigneesInLoadedRows =
    hasReviewerAssignees(allConversationsData)
  const userHiddenColumnsRef = useRef<Set<string>>(new Set())
  // Scoped by org + workspace so a workspace switch can't read another
  // workspace's saved column visibility.
  const currentStateStorageKey = useScopedStorageKey("monitoring-current-state")

  // Initialize visible columns from auto-saved state (synchronous — no flash).
  const [visibleColumns, setVisibleColumns] = useState<Set<string>>(() =>
    deriveVisibleColumnsFromSavedState(
      currentStateStorageKey,
      allConversationsData,
    ),
  )

  // WorkspaceProviderRoute deliberately keeps this hook's owner mounted
  // through an organization switch, so `currentStateStorageKey` can change
  // under column state that still reflects the previous organization. The
  // auto-add effect below only ever adds discovered metric columns — it
  // never resets stale ones — so without this, a rescope leaves the old
  // organization's visible/hidden columns showing under the new one. Re-run
  // the same derivation the initializer used, against the new key.
  const allConversationsDataRef = useRef(allConversationsData)
  allConversationsDataRef.current = allConversationsData
  const lastStorageKeyRef = useRef(currentStateStorageKey)
  useEffect(() => {
    if (lastStorageKeyRef.current === currentStateStorageKey) {
      return
    }
    lastStorageKeyRef.current = currentStateStorageKey
    userHiddenColumnsRef.current.clear()
    setVisibleColumns(
      deriveVisibleColumnsFromSavedState(
        currentStateStorageKey,
        allConversationsDataRef.current,
      ),
    )
  }, [currentStateStorageKey])

  // Auto-add metric columns from data when data loads.
  // Columns that the user (or a loaded view) explicitly hid are tracked in
  // userHiddenColumnsRef and won't be re-added.
  useEffect(() => {
    const metricKeysInData = new Set<string>()
    if (allConversationsData.length > 0) {
      allConversationsData.forEach((item) => {
        Object.keys(item.metrics || {}).forEach((key) => {
          metricKeysInData.add(`metric_${key}`)
        })
      })

      defaultConversationMetrics.forEach((key) => {
        metricKeysInData.add(`metric_${key}`)
      })
    }

    setVisibleColumns((prev) => {
      const newSet = new Set(prev)
      let addedAny = false

      if (
        hasReviewerAssigneesInLoadedRows &&
        !newSet.has(REVIEWER_COLUMN_KEY) &&
        !userHiddenColumnsRef.current.has(REVIEWER_COLUMN_KEY)
      ) {
        newSet.add(REVIEWER_COLUMN_KEY)
        addedAny = true
      }

      if (
        hasClusterDescriptions &&
        !newSet.has(CLUSTER_DESCRIPTION_COLUMN_KEY) &&
        !userHiddenColumnsRef.current.has(CLUSTER_DESCRIPTION_COLUMN_KEY)
      ) {
        newSet.add(CLUSTER_DESCRIPTION_COLUMN_KEY)
        addedAny = true
      }

      metricKeysInData.forEach((metricCol) => {
        if (
          !newSet.has(metricCol) &&
          !userHiddenColumnsRef.current.has(metricCol)
        ) {
          newSet.add(metricCol)
          addedAny = true
        }
      })
      return addedAny ? newSet : prev
    })
  }, [
    allConversationsData,
    defaultConversationMetrics,
    hasClusterDescriptions,
    hasReviewerAssigneesInLoadedRows,
  ])

  const handleVisibleColumnsReset = useCallback(
    (columns: Set<string>) => {
      const nextColumns = new Set(
        [...columns].filter((column) => !column.startsWith("metadata_")),
      )

      // Treat any known metric NOT in the incoming set as user-hidden,
      // so the auto-add effect won't re-add them after a view loads.
      userHiddenColumnsRef.current.clear()
      const allKnownMetricCols = new Set<string>()
      allConversationsData.forEach((item) => {
        Object.keys(item.metrics || {}).forEach((key) => {
          allKnownMetricCols.add(`metric_${key}`)
        })
      })
      defaultConversationMetrics.forEach((key) => {
        allKnownMetricCols.add(`metric_${key}`)
      })
      allKnownMetricCols.forEach((col) => {
        if (!nextColumns.has(col)) {
          userHiddenColumnsRef.current.add(col)
        }
      })
      if (!nextColumns.has(REVIEWER_COLUMN_KEY)) {
        userHiddenColumnsRef.current.add(REVIEWER_COLUMN_KEY)
      }
      if (!nextColumns.has(CLUSTER_DESCRIPTION_COLUMN_KEY)) {
        userHiddenColumnsRef.current.add(CLUSTER_DESCRIPTION_COLUMN_KEY)
      }

      setVisibleColumns(nextColumns)
    },
    [allConversationsData, defaultConversationMetrics],
  )

  const clearUserHiddenColumns = useCallback(() => {
    userHiddenColumnsRef.current.clear()
  }, [])

  // Reset to the "no view" default: base columns + all metrics from current data.
  // Clears userHidden so nothing is suppressed. Called by clearView in the context.
  const resetToDefaults = useCallback(() => {
    userHiddenColumnsRef.current.clear()

    const defaultColumns = getDefaultBaseColumns(allConversationsData)
    if (hasClusterDescriptions) {
      defaultColumns.add(CLUSTER_DESCRIPTION_COLUMN_KEY)
    }
    allConversationsData.forEach((item) => {
      Object.keys(item.metrics || {}).forEach((key) => {
        defaultColumns.add(`metric_${key}`)
      })
    })
    defaultConversationMetrics.forEach((key) => {
      defaultColumns.add(`metric_${key}`)
    })

    setVisibleColumns(defaultColumns)
  }, [allConversationsData, defaultConversationMetrics, hasClusterDescriptions])

  // Refs for column getters (auto-save reads these)
  const visibleColumnsRef = useRef(visibleColumns)
  visibleColumnsRef.current = visibleColumns

  // Register column callbacks so the context can set columns when loading views
  useEffect(() => {
    const callbacks: ColumnCallbacks = {
      onVisibleColumnsChange: handleVisibleColumnsReset,
      clearUserHiddenColumns,
      resetToDefaults,
      getVisibleColumns: () => visibleColumnsRef.current,
    }
    return registerColumnCallbacks(callbacks)
  }, [
    registerColumnCallbacks,
    handleVisibleColumnsReset,
    clearUserHiddenColumns,
    resetToDefaults,
  ])

  return {
    visibleColumns,
    hasReviewerAssigneesInLoadedRows,
    handleVisibleColumnsReset,
  }
}
